import React, { useCallback } from 'react';
import { Gender, GuestDetail } from '@camp67/model';
import clsx from 'clsx';
import { MessageKey } from '../../locales/en';
import { FormattedString, useMessages } from '../../i18n/useMessages';

type OnGuestChange = <T extends keyof GuestDetail>(
  id: string,
  key: T,
  value: GuestDetail[T],
) => void;

interface GuestListProps {
  totalTentCapacity: number;
  guestCount: number;
  guests: GuestDetail[];
  primaryContactId: string;
  onGuestChange: (id: string, details: Partial<GuestDetail>) => void;
}

export const GuestList: React.FC<GuestListProps> = ({
  totalTentCapacity: totalTentCapacity,
  guestCount,
  guests,
  primaryContactId,
  onGuestChange,
}) => {
  const totalAdditionalBedCards = Math.max(0, totalTentCapacity - guestCount);
  const getMessage = useMessages();

  const onChange: OnGuestChange = useCallback(
    (id, key, value) => {
      onGuestChange(id, { [key]: value });
    },
    [onGuestChange],
  );

  const isGuestCountExceedingRoomCapacity = guestCount > totalTentCapacity;

  if (isGuestCountExceedingRoomCapacity) {
    return (
      <div className="border-red-200 bg-red-50 border-2 rounded-md mt-2 py-3 px-3">
        <p className="text-red-400 text-lg font-medium">
          <FormattedString
            message="camp67-reservation.form.step-3.too-many-guests"
            values={{
              totalTentCapacity,
              guestCount,
            }}
            components={{
              strong: <strong className="font-extrabold" />,
              br: <br />,
              tentCapacityCode: <code className="px-2 py-1 bg-red-100 rounded-sm" />,
              roomsCapacityCode: <code className="px-2 py-1 bg-red-100 rounded-sm" />,
              guestsCountCode: <code className="px-2 py-1 bg-red-100 rounded-sm" />,
              bookingsCode: (
                <code className="text-gray-800 px-2 py-1 bg-gray-100 rounded-sm" />
              ),
            }}
          />
        </p>
      </div>
    );
  }

  return (
    <div>
      {guests.map((guest) => (
        <GuestCard
          {...guest}
          key={guest.id}
          onChange={onChange}
          isPrimaryContact={guest.id === primaryContactId}
        />
      ))}
      {totalAdditionalBedCards > 0
        ? Array.from(Array(totalAdditionalBedCards)).map((_, index) => (
            <div
              key={index}
              className="w-full py-3 px-5 bg-gray-100 rounded-md text-sm mt-3 uppercase tracking-widest text-gray-400"
            >
              {getMessage('camp67-reservation.form.step-3.additional-bed')}
            </div>
          ))
        : null}
    </div>
  );
};

type GuestCardProps = GuestDetail & {
  onChange: OnGuestChange;
  isPrimaryContact: boolean;
};

const genderLabels: Record<Gender, MessageKey> = {
  male: 'camp67-reservation.form.step-3.gender-male',
  female: 'camp67-reservation.form.step-3.gender-female',
  none: 'camp67-reservation.form.step-3.gender-none',
  'non-binary': 'camp67-reservation.form.step-3.gender-non-binary',
  'prefer-not-to-say': 'camp67-reservation.form.step-3.gender-prefer-not-to-say',
};
const genders: Gender[] = ['male', 'female', 'non-binary', 'prefer-not-to-say'];

const GuestCard: React.FC<GuestCardProps> = ({
  id,
  name,
  age,
  gender,
  onChange,
  isPrimaryContact,
}) => {
  const getMessage = useMessages();

  return (
    <div
      className={clsx(
        'w-full py-2 md:py-0 flex flex-col md:flex-row items-center gap-2 md:gap-4 rounded-md text-base mt-3',
        {
          'md:bg-gray-100': !isPrimaryContact,
          'md:bg-orange-100': isPrimaryContact,
        },
      )}
    >
      <input
        onChange={(e) => onChange(id, 'name', e.target.value)}
        value={name}
        type="text"
        className={clsx(
          'py-3 px-5 rounded-md w-full md:w-1/2 md:h-full md:py-3 placeholder:text-gray-400 outline-none border-none',
          {
            'bg-gray-100 md:bg-transparent': !isPrimaryContact,
            'bg-orange-100 md:bg-transparent': isPrimaryContact,
          },
        )}
        placeholder={getMessage('camp67-reservation.form.step-3.guests.enter-name')}
      />
      <input
        type="number"
        className={clsx(
          'md:ml-0 px-5 rounded-md md:rounded-none py-3 w-full md:w-1/4 md:h-full md:py-3 md:px-2 outline-none md:focus-within:bg-blue-100',
          {
            'bg-gray-100 md:bg-transparent': !isPrimaryContact,
            'bg-orange-100 md:bg-transparent': isPrimaryContact,
          },
        )}
        placeholder={getMessage('camp67-reservation.form.step-3.guests.enter-age')}
        required={true}
        onChange={(e) => onChange(id, 'age', parseInt(e.target.value))}
        onWheelCapture={(e) => {
          e.currentTarget.blur();
        }}
        value={age !== 0 ? age : ''}
        min={1}
        max={120}
      />
      <div
        className={clsx(
          'px-5 py-3 md:py-3 md:px-2 w-full md:w-1/4 md:h-full rounded-md md:rounded-none md:rounded-r-md md:focus-within:bg-blue-100',
          {
            'bg-gray-100 md:bg-transparent': !isPrimaryContact,
            'bg-orange-100 md:bg-transparent': isPrimaryContact,
          },
        )}
      >
        <select
          value={gender}
          onChange={(e) => onChange(id, 'gender', e.target.value as Gender)}
          className={clsx('-ml-1.5 md:ml-0 w-full bg-transparent outline-none', {
            'text-gray-400': gender === 'none',
          })}
        >
          <option value={'none'} label={'Select gender'} disabled>
            Select gender
          </option>
          {genders.map((gender, key) => (
            <option key={key} value={gender} label={getMessage(genderLabels[gender])}>
              {getMessage(genderLabels[gender])}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};
